import {useCallback, useEffect, useRef, useState} from "react";
import {WebrtcRepository} from "../repositories/WebrtcRepository";

const OPEN_POLL_MS = 4000;
const CLOSED_POLL_MS = 15000;

// Callers waiting in the project's agent groups. There is no websocket topic for the queue, so it is polled: only the
// counts while the phone panel is closed (detailed: false), the callers too while it is open.
// -> {groups: null until loaded, waiting: [{position, wait_seconds, caller_id, conversation_id, group}], count,
// unavailable: Asterisk cannot be reached (its live counts are null)}
export const useAgentQueue = ({projectId, enabled, detailed}) => {
  const [groups, setGroups] = useState(null);
  const [waiting, setWaiting] = useState([]);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    try {
      const {data} = await WebrtcRepository.getMyGroups({projectId});
      const items = data?.groups || [];
      const busy = detailed ? items.filter(group => Number(group.waiting_count) > 0) : [];
      const details = await Promise.all(busy.map(group =>
        WebrtcRepository.getGroup({projectId, groupId: group.id})
          .then(response => (response.data?.waiting || []).map(caller => ({...caller, group})))
          .catch(e => {
            console.error("Failed to load the waiting callers", e);
            return [];
          })
      ));
      // a newer poll (or the panel closing) wins
      if (requestId !== requestRef.current) return;
      setGroups(items);
      setWaiting(details.flat().sort((a, b) => (b.wait_seconds || 0) - (a.wait_seconds || 0)));
    } catch (e) {
      console.error("Failed to load the agent groups", e);
    }
  }, [projectId, detailed]);

  useEffect(() => {
    if (!enabled || !projectId) return;
    load();
    const id = setInterval(load, detailed ? OPEN_POLL_MS : CLOSED_POLL_MS);
    return () => {
      requestRef.current += 1;
      clearInterval(id);
    };
  }, [enabled, projectId, detailed, load]);

  return {
    groups,
    waiting,
    count: (groups || []).reduce((sum, group) => sum + (Number(group.waiting_count) || 0), 0),
    unavailable: Boolean(groups?.length) && groups.every(group => group.waiting_count === null || group.waiting_count === undefined),
  };
};
